/**
 * The capture queue as React state: what is waiting on this phone, what was just sent, and
 * sending whenever there is a chance - on open, when the connection returns, when the app comes
 * back to the foreground, and right after a capture.
 */
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { documentKeys } from "@/hooks/use-documents";
import { sendCapture, type CaptureContext } from "@/lib/api/capture";
import {
  flushQueue,
  indexedDbStore,
  memoryStore,
  type QueuedCapture,
  type SendOutcome,
} from "@/lib/capture/capture-queue";
import { track } from "@/lib/platform/analytics";

export type SentCapture = { id: string; kind: QueuedCapture["kind"]; outcome: SendOutcome };

export function useCaptureQueue(context: CaptureContext) {
  // jsdom (unit tests) has no IndexedDB; the page still works, it just forgets on reload.
  const store = useMemo(
    () => (typeof indexedDB === "undefined" ? memoryStore() : indexedDbStore()),
    [],
  );
  const queryClient = useQueryClient();
  const [waiting, setWaiting] = useState<QueuedCapture[]>([]);
  const [sent, setSent] = useState<SentCapture[]>([]);
  const [sending, setSending] = useState(false);
  const busy = useRef(false);
  // Sending reads the latest workspace and user without restarting the listeners below.
  const contextRef = useRef(context);
  useEffect(() => {
    contextRef.current = context;
  });

  const refresh = useCallback(async () => {
    setWaiting((await store.list()).sort((a, b) => a.createdAt.localeCompare(b.createdAt)));
  }, [store]);

  const flush = useCallback(async () => {
    if (busy.current || !contextRef.current.userId) return;
    busy.current = true;
    setSending(true);
    try {
      const result = await flushQueue(
        store,
        (item) =>
          sendCapture(item, contextRef.current, (documentId) => store.put({ ...item, documentId })),
        (item, outcome) => {
          setSent((current) =>
            [{ id: item.id, kind: item.kind, outcome }, ...current].slice(0, 10),
          );
          track("receipt_captured", { kind: item.kind, pages: item.pages.length });
        },
      );
      if (result.sent > 0) await queryClient.invalidateQueries({ queryKey: documentKeys.all });
    } finally {
      busy.current = false;
      setSending(false);
      await refresh();
    }
  }, [queryClient, refresh, store]);

  useEffect(() => {
    // The queue lives in IndexedDB, outside React: load it, then send what is waiting.
    void Promise.resolve().then(refresh).then(flush);
    const onOnline = () => void flush();
    const onVisible = () => {
      if (document.visibilityState === "visible") void flush();
    };
    window.addEventListener("online", onOnline);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("online", onOnline);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [flush, refresh]);

  const enqueue = useCallback(
    async (capture: Omit<QueuedCapture, "id" | "createdAt" | "attempts">) => {
      await store.put({
        ...capture,
        id: crypto.randomUUID(),
        createdAt: new Date().toISOString(),
        attempts: 0,
      });
      await refresh();
      void flush();
    },
    [flush, refresh, store],
  );

  /** Drops a receipt that cannot be sent, such as one whose photos can no longer be read. */
  const discard = useCallback(
    async (id: string) => {
      await store.remove(id);
      await refresh();
    },
    [refresh, store],
  );

  return { waiting, sent, sending, enqueue, flush, discard };
}
